/**
 * The degradation ladder. SPEC §3.2.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ANCHOR NEVER SILENTLY REPOINTS.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Four tiers, evaluated at READ time, in this exact order:
 *
 * | Condition                                                   | Tier | Behaviour |
 * |-------------------------------------------------------------|------|-----------|
 * | `ayahId` invalid (corrupted import only)                      | 4    | Quarantine the row; show `quote` + raw `ref` on the Repair screen. Never delete. |
 * | Word count for the ayah changed                               | 3    | Degrade a word anchor to whole-ayah, `drift = 1`, surface in "Review N shifted highlights" with the stored `quote` beside the current text. |
 * | `anchor_space_version` differs from THIS BUILD'S, for a `words` anchor | 3 | Forced review. `from_import` chooses the UI copy, not the tier. See the note at the call site. |
 * | no stored hashes, for a `words` anchor                        | 1b   | Degrade to whole-ayah + the quiet marker. Nothing to verify the indices with. |
 * | span not derivable, for a `words` anchor                      | 3    | Degrade to whole-ayah + review. SPEC §3.2's renderer gate. |
 * | raw match + fold match                                        | 1    | Render exactly. No UI noise. >99% of reads. |
 * | raw mismatch + fold match                                     | 1b   | Render. Quiet dismissible "text corrected" marker. No drift flag. |
 * | fold mismatch, count unchanged, the quote's WORDS still occur in the span | 2 | Re-derive the span, render, log a local drift metric, stay silent. |
 * | fold mismatch, count unchanged, quote check fails              | 3    | Escalate. |
 *
 * THE ORDER IS PART OF THE CONTRACT. An anchor can qualify for several rows at
 * once — an invalid ayahId AND a changed word count AND a fold mismatch — and
 * the row it gets is the FIRST one in this table, top to bottom. Quarantine
 * outranks degradation, degradation outranks a silent re-derive, and the
 * strongest available signal is the one reported. `anchor.spec.ts` asserts this
 * for the multi-condition cases, not just the single-condition ones.
 *
 * WHY THE QUOTE CHECK EARNS ITS KEEP (SPEC §3.2)
 * ──────────────────────────────────────────────
 * A tokenisation override that JOINS one pair of words and SPLITS another is
 * net-zero on word count and shifts every index in the ayah. Nothing else in
 * the evidence set catches it: the count test passes, and the hashes only say
 * "different", not "shifted". The quote comparison is the only thing that
 * catches it, the quote is already stored, and it costs one `includes()`.
 *
 * "Ayah-level and range anchors carry no word span and therefore cannot reach
 * tier 3. A whole-ayah note — the ~90% case — can never break." That sentence is
 * enforced here, not merely documented: `classifyDrift` returns tier 1 or 1b for
 * an `ayah` or `range` anchor whose ayahId is valid, whatever the hashes say
 * about the text, because there is no index that could have shifted.
 *
 * PURITY
 * ──────
 * `classifyDrift` is pure and synchronous. Hashing is async, so the caller
 * computes the two current hashes and hands them in as `DriftEvidence`. That
 * also means the ladder is testable without a crypto implementation, and that a
 * read path can batch its hashing.
 *
 * FLAGS
 * ─────
 * Every flag on the way in and on the way out is `0 | 1`, never a boolean and
 * never `null`: IndexedDB cannot index booleans, and a row whose indexed keypath
 * is `null` is simply ABSENT from that index (SPEC §3.3, invariant 3). Keeping
 * the ladder's own vocabulary in the storable shape means a verdict can be
 * written to a row without a translation step where a `false` could become an
 * absent index entry.
 */

import { ANCHOR_SPACE_VERSION, type Anchor } from './types';
import { fold, segment } from './segment';
import { canRenderWordAnchors } from './wordmap';

/**
 * The anchor space this build reads in, named for what the comparison in
 * `classifyDrift` means, so a future reader does not have to guess which side
 * of it is "now".
 */
export const ANCHOR_SPACE_VERSION_AT_READ = ANCHOR_SPACE_VERSION;

/** The four tiers, with 1b as its own value because it has its own behaviour. */
export type DriftTier = 1 | '1b' | 2 | 3 | 4;

/** What the caller measured about the anchor's current surroundings. */
export interface DriftEvidence {
	/** 0 when the `ref` does not name an ayah that exists — corrupted import. */
	ayah_id_valid: 0 | 1;
	/** `sha256(currentSpan).slice(0,16)`; `''` when the span cannot be derived. */
	current_hash_raw: string;
	/** `sha256(fold(currentSpan)).slice(0,16)`; `''` when not derivable. */
	current_hash_fold: string;
	/** Canonical word count of the anchored ayah in the text on this device. */
	current_word_count: number;
	/** Canonical word count of the same ayah in the frozen fixture. */
	fixture_word_count: number;
	/** The re-derived span, verbatim, for the tier-2 quote test. */
	current_span: string;
	/**
	 * 1 while evaluating rows that arrived in an import, 0 on an ordinary read.
	 *
	 * DELIBERATELY NOT READ BY THE LADDER ANY MORE. It used to gate the
	 * anchor-space escalation, which scoped that rule to imports and therefore
	 * missed the common case — see the note at that branch. It stays on the
	 * evidence because the CALLER needs it to choose the UI copy ("forced
	 * review on import" versus "review after upgrade"), and because removing a
	 * field from a frozen evidence shape costs more than keeping it.
	 */
	from_import: 0 | 1;
}

/** The verdict. Storable as-is. */
export interface DriftVerdict {
	tier: DriftTier;
	/** SPEC §3.3: `drift` is set at READ time, never auto-fixed, never auto-cleared. */
	drift: 0 | 1;
	/** 1 ⇒ render the whole ayah instead of the word span. */
	degrade_to_ayah: 0 | 1;
	/** 1 ⇒ do not render; show `quote` + raw `ref` on the Repair screen. */
	quarantine: 0 | 1;
	/** 1 ⇒ the quiet dismissible "text corrected" marker. Tier 1b only. */
	show_corrected_marker: 0 | 1;
	/** 1 ⇒ list the row in "Review N shifted highlights". */
	needs_review: 0 | 1;
	/** A stable machine-readable reason. Never shown raw to the user. */
	reason: DriftReason;
}

export type DriftReason =
	| 'ok'
	| 'ayah_id_invalid'
	| 'word_count_changed'
	| 'anchor_space_version_changed'
	| 'text_corrected'
	| 'quote_relocated'
	| 'quote_lost'
	| 'no_hashes_stored'
	| 'span_not_derivable';

function verdict(
	tier: DriftTier,
	reason: DriftReason,
	overrides: Partial<Omit<DriftVerdict, 'tier' | 'reason'>> = {}
): DriftVerdict {
	return {
		tier,
		drift: 0,
		degrade_to_ayah: 0,
		quarantine: 0,
		show_corrected_marker: 0,
		needs_review: 0,
		reason,
		...overrides
	};
}

/**
 * Run the ladder. Pure; no clock, no I/O, no mutation of `anchor`.
 *
 * `anchor` is the STORED anchor — the one whose `quote` and hashes were minted
 * when the remark was written. `evidence` describes the world as it is now.
 */
export function classifyDrift(anchor: Anchor, evidence: DriftEvidence): DriftVerdict {
	/* ── Tier 4 — the row does not name a place that exists. ────────────────
	 * Quarantine, never delete. The user's sentence is still in the row; only
	 * our ability to place it is gone. SPEC §3.2, last row. */
	if (evidence.ayah_id_valid === 0) {
		return verdict(4, 'ayah_id_invalid', { drift: 1, quarantine: 1, needs_review: 1 });
	}

	/* ── Anchors with no word span cannot shift. ────────────────────────────
	 * `free` and `external` have no place in the mushaf at all; `ayah` and
	 * `range` have a place but no index inside it. SPEC §3.2: "Ayah-level and
	 * range anchors carry no word span and therefore cannot reach tier 3."
	 * They can still report 1b, because a corrected harakat is worth a quiet
	 * marker on a whole-ayah note too. */
	const hasWordSpan = anchor.kind === 'words';
	if (!hasWordSpan) {
		return classifyHashes(anchor, evidence, false);
	}

	/* ── Tier 3 — the word count for this ayah changed. ─────────────────────
	 * Every index inside the ayah is now suspect. Degrade to whole-ayah, flag,
	 * and surface for one-tap re-placement with the stored quote beside the
	 * current text. SPEC §3.2. */
	if (evidence.current_word_count !== evidence.fixture_word_count) {
		return verdict(3, 'word_count_changed', {
			drift: 1,
			degrade_to_ayah: 1,
			needs_review: 1
		});
	}

	/* ── Tier 3 — a `words` anchor from a different anchor space. ────────────
	 *
	 * SPEC DISAGREEMENT, IMPLEMENTED DELIBERATELY. §3.2 row 6 words this rule as
	 * "`anchor_space_version` in an IMPORTED FILE differs". Scoped that way it
	 * misses the common case rather than the rare one: after an in-place app
	 * upgrade that bumps the anchor space, EVERY LOCAL ROW is from the old
	 * space, and none of them arrived in an import. The word-count check above
	 * cannot catch it either — after a bump, `current_word_count` and
	 * `fixture_word_count` are both the NEW value and compare equal.
	 *
	 * Gating this on `from_import` was therefore the concrete path by which a
	 * word anchor silently repoints after a routine app update, which is the
	 * exact thing this file's banner forbids. The rule the sentence has to mean
	 * is "differs from the space THIS BUILD READS IN", with `from_import`
	 * surviving only as the flag that chooses the UI copy ("forced review on
	 * import" versus "review after upgrade") — a caller-side concern, since the
	 * caller supplied it.
	 *
	 * This is also where SPEC §3.2's renderer gate is ENFORCED rather than
	 * merely exported: `canRenderWordAnchors` is the same predicate a surface
	 * must consult before drawing a word span, and calling it here means the
	 * ladder and the renderer cannot disagree about when word anchors are
	 * permitted.
	 *
	 * An anchor carrying NO `anchor_space_version` at all is not handled here —
	 * absence is not "a different version". It falls through to the evidence
	 * check below, which degrades it to ayah level for exactly that reason. */
	if (
		anchor.anchor_space_version !== undefined &&
		!canRenderWordAnchors(anchor.anchor_space_version)
	) {
		return verdict(3, 'anchor_space_version_changed', {
			drift: 1,
			degrade_to_ayah: 1,
			needs_review: 1
		});
	}

	return classifyHashes(anchor, evidence, true);
}

/**
 * Tiers 1, 1b, 2 and the quote-failure branch of tier 3.
 *
 * `canEscalate` is false for anchors with no word span: for those, a hash
 * mismatch means the ayah's text changed, not that our indices moved, so the
 * honest outcome is "render it, note that the text moved" rather than a review
 * queue the user cannot act on.
 */
function classifyHashes(
	anchor: Anchor,
	evidence: DriftEvidence,
	canEscalate: boolean
): DriftVerdict {
	const storedRaw = anchor.text_hash_raw;
	const storedFold = anchor.text_hash_fold;

	/* ── ABSENT EVIDENCE, FOR AN ANCHOR THAT HAS A WORD SPAN ────────────────
	 *
	 * Layer 3 is optional in the type because a `free` anchor has no span to
	 * hash and because a row imported from a pre-Layer-3 file has none. The
	 * comment here used to say "absent evidence is not evidence of soundness:
	 * report it, do not claim tier 1" — and then the code returned tier 1, with
	 * `drift`, `degrade_to_ayah`, `quarantine`, `show_corrected_marker` and
	 * `needs_review` all 0, even for a `words` anchor.
	 *
	 * A caller that switches on `tier` — the natural thing, and what tier means
	 * in SPEC §3.2: "Render exactly. No UI noise." — would then render a word
	 * span with ZERO evidence that it points at the right words. For
	 * `span_not_derivable` that is literally the inverse of the spec's renderer
	 * gate: "a renderer that cannot supply a wordmap to the canonical
	 * segmentation may not render word anchors; it degrades to ayah level with a
	 * visible note."
	 *
	 * So, for a `words` anchor, both reasons DEGRADE TO AYAH with something
	 * visible:
	 *   - `no_hashes_stored`   → 1b + the quiet marker. The ayah is still the
	 *                            right place; only the word indices are
	 *                            unverifiable, and there is nothing the user can
	 *                            act on, so this is not a review item.
	 *   - `span_not_derivable` → tier 3 + review. The renderer genuinely cannot
	 *                            place it, and the user can.
	 * The clean tier-1 shape survives only for `ayah`, `range`, `free` and
	 * `external` — the kinds with no index that could have shifted. */
	if (!storedRaw || !storedFold) {
		if (!canEscalate) return verdict(1, 'no_hashes_stored');
		return verdict('1b', 'no_hashes_stored', {
			degrade_to_ayah: 1,
			show_corrected_marker: 1
		});
	}
	if (!evidence.current_hash_raw || !evidence.current_hash_fold) {
		if (!canEscalate) return verdict(1, 'span_not_derivable');
		return verdict(3, 'span_not_derivable', {
			drift: 1,
			degrade_to_ayah: 1,
			needs_review: 1
		});
	}

	const rawMatches = storedRaw === evidence.current_hash_raw;
	const foldMatches = storedFold === evidence.current_hash_fold;

	/* ── Tier 1 — nothing changed. >99% of reads. Silent. ──────────────────── */
	if (rawMatches && foldMatches) return verdict(1, 'ok');

	/* ── Tier 1b — the diacritics were corrected, the words are the same. ────
	 * This is the case that only exists because TWO hashes are stored. With
	 * one folded hash it is indistinguishable from tier 1, and a Tanzil harakat
	 * revision passes unremarked. SPEC §3.1. */
	if (foldMatches) {
		return verdict('1b', 'text_corrected', { show_corrected_marker: 1 });
	}

	if (!canEscalate) {
		// An ayah or range note whose ayah text changed. Render it; there is no
		// index to have shifted and nothing for the user to re-place.
		return verdict('1b', 'text_corrected', { show_corrected_marker: 1 });
	}

	/* ── Tier 2 — the folded text moved but our words are still in there. ────
	 * `fold(quote)` being a substring of `fold(re-derived span)` says the span
	 * we would render still contains the words the user selected. Re-derive,
	 * render, log a local metric, stay silent. SPEC §3.2. */
	const quote = anchor.quote;
	if (quote) {
		if (quoteStillInSpan(quote, evidence.current_span)) {
			return verdict(2, 'quote_relocated');
		}
		/* ── Tier 3 — the quote check failed. ──────────────────────────────
		 * The join-one-split-one tokenisation change: net-zero on count, every
		 * index shifted. This branch is the only thing in the ladder that
		 * catches it. */
		return verdict(3, 'quote_lost', { drift: 1, degrade_to_ayah: 1, needs_review: 1 });
	}

	// No quote to check with. Escalate rather than guess: the design's promise
	// is that the anchor never silently repoints.
	return verdict(3, 'quote_lost', { drift: 1, degrade_to_ayah: 1, needs_review: 1 });
}

/**
 * The minimum canonical words a stored `quote` must carry before tier 2 — a
 * SILENT re-derive — is reachable at all.
 *
 * One word is not evidence. Arabic function words (فِى, مِن, لَا) recur dozens
 * of times inside a single ayah, so a one-word quote matching somewhere in a
 * shifted span says almost nothing about whether the span is the user's span.
 * A quote that carries less than this escalates to tier 3, where the user is
 * shown the stored quote beside the current text and re-places it in one tap.
 * Costing the user a tap is the cheap error; repointing their remark at
 * different words and saying nothing is the expensive one.
 */
export const TIER_2_MIN_QUOTE_WORDS = 2;

/**
 * The tier-2 test: are the words the user selected still inside the span we
 * would now render?
 *
 * ON CANONICAL WORD BOUNDARIES, NOT CHARACTERS. SPEC §3.2 writes this as
 * "`fold(quote)` is a substring of `fold(re-derived span)`", and a raw
 * `includes()` is a demonstrable false positive: the stored quote فِى folds to
 * في, the re-derived span ٱلْوَافِى شَىْءٍ folds to الوافي شيء, and في sits
 * INSIDE الوافي. `includes()` is true, the ladder returns tier 2, and the
 * renderer draws a different span silently — the exact thing this file's banner
 * forbids.
 *
 * So the comparison is a CONTIGUOUS SUBSEQUENCE test over `segment(fold(·))`:
 * the quote's canonical words must appear, in order, as whole words of the
 * span's canonical words. That is what "the words the user selected are still
 * in there" means, and it is what the sentence in the spec was reaching for.
 *
 * Pure, and exported so the read path and the Repair screen can explain a
 * verdict with the same predicate that produced it.
 */
export function quoteStillInSpan(quote: string, currentSpan: string): boolean {
	const needle = segment(fold(quote));
	if (needle.length < TIER_2_MIN_QUOTE_WORDS) return false;

	const hay = segment(fold(currentSpan));
	if (needle.length > hay.length) return false;

	for (let start = 0; start + needle.length <= hay.length; start++) {
		let all = true;
		for (let i = 0; i < needle.length; i++) {
			if (hay[start + i] !== needle[i]) {
				all = false;
				break;
			}
		}
		if (all) return true;
	}
	return false;
}
